import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type OnboardingStep =
  | 'welcome'
  | 'profile'
  | 'import_contacts'
  | 'work_hours'
  | 'preferences'
  | 'complete';

interface OnboardingState {
  // Whether onboarding has been completed
  completed: boolean;
  // Current step in the onboarding flow
  currentStep: OnboardingStep;
  // Whether the onboarding modal is open
  isOpen: boolean;
  // Collected data during onboarding
  data: {
    displayName?: string;
    jobTitle?: string;
    importSource?: 'google' | 'csv' | 'skip';
    contactsImported?: number;
  };

  // Actions
  open: () => void;
  close: () => void;
  dismiss: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: OnboardingStep) => void;
  setData: (data: Partial<OnboardingState['data']>) => void;
  complete: () => void;
  reset: () => void;
}

const STEP_ORDER: OnboardingStep[] = [
  'welcome',
  'profile',
  'import_contacts',
  'work_hours',
  'preferences',
  'complete',
];

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      completed: false,
      currentStep: 'welcome',
      isOpen: false,
      data: {},

      open: () => set({ isOpen: true }),

      close: () => set({ isOpen: false }),

      dismiss: () => set({ isOpen: false }),

      nextStep: () => {
        const { currentStep } = get();
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex < STEP_ORDER.length - 1) {
          set({ currentStep: STEP_ORDER[currentIndex + 1] });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex > 0) {
          set({ currentStep: STEP_ORDER[currentIndex - 1] });
        }
      },

      goToStep: (step) => set({ currentStep: step }),

      setData: (newData) => set((state) => ({
        data: { ...state.data, ...newData },
      })),

      complete: () => set({
        completed: true,
        isOpen: false,
        currentStep: 'complete',
      }),

      reset: () => set({
        completed: false,
        currentStep: 'welcome',
        isOpen: true,
        data: {},
      }),
    }),
    {
      name: 'ondeck-onboarding',
    }
  )
);

// Helper to get step number (1-indexed for display)
export function getStepNumber(step: OnboardingStep): number {
  return STEP_ORDER.indexOf(step) + 1;
}

// Helper to get total steps (excluding 'complete')
export function getTotalSteps(): number {
  return STEP_ORDER.length - 1; // Don't count 'complete' as a step
}
